import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// ─── ScrollToTop ──────────────────────────────────────────────────────────────
// Floating arrow-up button on inner pages (Photography, Videography, Store).
// Appears after scrolling 320px down; smooth-scrolls the page to top on click.
// ─────────────────────────────────────────────────────────────────────────────

export default function ScrollToTop() {
  const { pathname } = useLocation();
  const isInnerPage = pathname !== '/';
  const [visible, setVisible] = useState(false);

  const handleScroll = useCallback(() => {
    setVisible(window.scrollY > 320);
  }, []);

  useEffect(() => {
    if (!isInnerPage) { setVisible(false); return; }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // check immediately on route change
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isInnerPage, handleScroll]);

  if (!isInnerPage) return null;

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <button
      onClick={handleClick}
      aria-label="Back to top"
      style={{
        position:        'fixed',
        bottom:          32,
        right:           32,
        zIndex:          9000,
        width:           44,
        height:          44,
        borderRadius:    '50%',
        border:          '1px solid rgba(242,239,232,0.22)',
        background:      'rgba(10,10,10,0.72)',
        backdropFilter:  'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        color:           'rgba(242,239,232,0.75)',
        cursor:          'pointer',
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        padding:         0,
        opacity:         visible ? 1 : 0,
        transform:       visible ? 'translateY(0)' : 'translateY(14px)',
        transition:      'opacity 280ms ease, transform 280ms ease, background 180ms ease, border-color 180ms ease',
        pointerEvents:   visible ? 'auto' : 'none',
        boxShadow:       '0 4px 20px rgba(0,0,0,0.45)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255,42,31,0.88)';
        e.currentTarget.style.borderColor = 'rgba(255,42,31,0.7)';
        e.currentTarget.style.color = '#fff';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(10,10,10,0.72)';
        e.currentTarget.style.borderColor = 'rgba(242,239,232,0.22)';
        e.currentTarget.style.color = 'rgba(242,239,232,0.75)';
      }}
    >
      {/* Arrow up SVG */}
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="18 15 12 9 6 15" />
      </svg>
    </button>
  );
}
